import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Tag } from 'lucide-react';
import { toast } from 'react-hot-toast';
import apiService from '../services/api';
import { Category, CreateCategoryForm, UpdateCategoryForm } from '../types';
import { useAuth } from '../contexts/AuthContext';
import DataGrid from '../components/grid/DataGrid';
import Modal from '../components/ui/Modal';
import CategoryForm from '../components/forms/CategoryForm';
import ConfirmDialog from '../components/modals/ConfirmDialog';

const CategoriesPage: React.FC = () => {
  const { token, user } = useAuth();
  const queryClient = useQueryClient();
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [formError, setFormError] = useState<string>('');

  // Queries
  const {
    data: categoriesResponse,
    isLoading,
    error
  } = useQuery({
    queryKey: ['categories', currentPage, pageSize, searchTerm, sortField, sortDirection],
    queryFn: () => apiService.getCategories(currentPage, pageSize, searchTerm, sortField, sortDirection),
    enabled: !!token
  });

  // Mutations
  const createCategoryMutation = useMutation({
    mutationFn: (data: CreateCategoryForm) => apiService.createCategory(data),
    onSuccess: (response) => {
      if (response.success && response.data) {
        toast.success('Category created successfully!');
        setShowCreateModal(false);
        setFormError('');
        queryClient.invalidateQueries({ 
          queryKey: ['categories'],
          exact: false 
        });
      } else {
        toast.error(response.message || 'Failed to create category');
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.response?.data?.errors?.join(', ') || 'Error creating category';
      toast.error(errorMessage);
      setFormError(errorMessage);
    }
  });

  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCategoryForm }) => apiService.updateCategory(id, data),
    onSuccess: (response) => {
      if (response.success && response.data) {
        toast.success('Category updated successfully!');
        setShowEditModal(false);
        setSelectedCategory(null);
        setFormError('');
        queryClient.invalidateQueries({ 
          queryKey: ['categories'],
          exact: false 
        });
      } else {
        toast.error(response.message || 'Failed to update category');
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.response?.data?.errors?.join(', ') || 'Error updating category';
      toast.error(errorMessage);
      setFormError(errorMessage);
    }
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteCategory(id),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Category deleted successfully!');
        setShowDeleteDialog(false);
        setSelectedCategory(null);
        queryClient.invalidateQueries({ 
          queryKey: ['categories'],
          exact: false 
        });
      } else {
        toast.error(response.message || 'Failed to delete category');
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Error deleting category';
      toast.error(errorMessage);
    }
  });

  // Handlers
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleCreate = (data: CreateCategoryForm) => {
    createCategoryMutation.mutate(data);
  };

  const handleEdit = useCallback((category: Category) => {
    setSelectedCategory(category);
    setShowEditModal(true);
    setFormError('');
  }, []);

  const handleUpdate = (data: UpdateCategoryForm) => {
    if (selectedCategory) {
      updateCategoryMutation.mutate({ id: selectedCategory.id, data });
    }
  };

  const handleDelete = useCallback((category: Category) => {
    setSelectedCategory(category);
    setShowDeleteDialog(true);
  }, []);

  const confirmDelete = () => {
    if (selectedCategory) {
      deleteCategoryMutation.mutate(selectedCategory.id);
    }
  };

  // Grid columns
  const columns = useMemo(() => [
    {
      id: 'name',
      header: 'Name',
      sortable: true,
      cell: (value: string) => (
        <div className="font-medium text-gray-900 dark:text-gray-100">{value}</div>
      )
    },
    {
      id: 'description',
      header: 'Description',
      sortable: true,
      cell: (value: string) => (
        <div className="text-gray-600 dark:text-gray-300 max-w-xs truncate" title={value}>
          {value || '-'}
        </div>
      )
    },
    {
      id: 'isActive',
      header: 'Status',
      sortable: true,
      cell: (value: boolean, row: Category) => (
        <div className="relative group">
          <div className="group-hover:opacity-0 transition-opacity duration-200">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              value 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {value ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-end gap-2 pr-2" onClick={(e)=>e.stopPropagation()}>
            <button 
              onClick={() => handleEdit(row)}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
              title="Edit Category"
            >
              <Edit className="h-4 w-4"/>
            </button>
            <button 
              onClick={() => handleDelete(row)}
              className="h-7 px-2 inline-flex items-center border rounded-md text-red-600 border-red-300 hover:bg-red-50"
              title="Delete Category"
            >
              <Trash2 className="h-4 w-4"/>
            </button>
          </div>
        </div>
      )
    }
  ], [handleEdit, handleDelete]);

  // Data for grid
  const categories = categoriesResponse?.data?.data || [];
  const totalCount = categoriesResponse?.data?.totalCount || 0;
  const totalPages = categoriesResponse?.data?.totalPages || 1;



  // Check authentication after all hooks are declared
  if (!token || !user) {
    console.log('Not authenticated, redirecting to login');
    window.location.href = '/login';
    return null;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 dark:text-red-400 text-lg font-medium">Error loading categories</div>
          <div className="text-gray-500 dark:text-gray-400 mt-2">Please try again</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <Tag className="h-8 w-8 text-gray-600 dark:text-gray-400" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Categories</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Manage work order categories</p>
          </div>
        </div>
        <button
          onClick={() => {
            setShowCreateModal(true);
            setFormError('');
          }}
          className="btn-primary"
        >
          <Plus size={20} className="mr-2" />
          Add Category
        </button>
      </div>

      {/* Grid */}
      <DataGrid
        data={categories}
        columns={columns}
        isLoading={isLoading}
        searchTerm={searchTerm}
        onSearch={handleSearch}
        currentPage={currentPage}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSort={handleSort}
        sortField={sortField}
        sortDirection={sortDirection}
        searchPlaceholder="Search categories..."
        emptyMessage="No categories found"
        onRowClick={(row) => {
          const category = row as Category;
          handleEdit(category);
        }}
        rowTooltip="Edit Category"
      />

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setFormError('');
        }}
        title="Add New Category"
      >
        <CategoryForm
          onSubmit={handleCreate}
          isLoading={createCategoryMutation.isPending}
          error={formError}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedCategory(null);
          setFormError('');
        }}
        title="Edit Category"
      >
        <CategoryForm
          category={selectedCategory}
          onSubmit={handleUpdate}
          isLoading={updateCategoryMutation.isPending}
          error={formError}
        />
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedCategory(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Category"
        message={`Are you sure you want to delete "${selectedCategory?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={deleteCategoryMutation.isPending}
      />
    </div>
  );
};

export default CategoriesPage;
